import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useTenant } from "@/lib/tenant";
import { useAuth } from "@/lib/auth";
import { PageHeader, Panel, SevBadge, Empty } from "@/components/Bits";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Brain, Sparkles, Columns3 } from "lucide-react";
import { toast } from "sonner";

export default function Incidents() {
  const { tenantId } = useTenant();
  const isCustomer = useAuth().user?.role === "customer";
  const [params, setParams] = useSearchParams();
  const openId = params.get("open");

  const { data = [] } = useQuery({
    queryKey: ["incidents", tenantId],
    queryFn: () => api.get(`/incidents/${tenantId}`).then((r) => r.data),
    enabled: !!tenantId,
  });

  const detail = useQuery({
    queryKey: ["incident", openId],
    queryFn: () => api.get(`/incident/${openId}`).then((r) => r.data),
    enabled: !!openId,
  });

  const [narratives, setNarratives] = useState({});
  const [model, setModel] = useState("claude-sonnet-5");
  const [compare, setCompare] = useState(false);
  const [busy, setBusy] = useState(false);

  const models = useQuery({ queryKey: ["ai-models"], queryFn: () => api.get("/ai/models").then((r) => r.data) });
  const modelList = models.data || [];
  const label = (id) => modelList.find((m) => m.id === id)?.label || id;

  useEffect(() => {
    setNarratives(detail.data?.incident?.ai_narratives || {});
    setCompare(false);
  }, [detail.data]);

  const askAI = useMutation({
    mutationFn: (cmp) => api.post(`/ai/soc`, { incident_id: openId, model, compare: cmp }).then((r) => r.data),
    onMutate: (cmp) => { setBusy(true); setCompare(cmp); if (!cmp) setNarratives((n) => ({ ...n, [model]: "" })); },
    onSuccess: (d) => {
      if (d.results) {
        setNarratives((n) => ({ ...n, ...Object.fromEntries(d.results.map((r) => [r.model, r.narrative || `⚠ ${r.error}`])) }));
        toast.success(`Side-by-side briefing: ${d.results.filter((r) => r.narrative).length}/${d.results.length} models responded`);
      } else {
        setNarratives((n) => ({ ...n, [d.model]: d.narrative }));
        toast.success(`AI briefing generated · ${label(d.model)}`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "AI SOC failed"),
    onSettled: () => setBusy(false),
  });

  if (!tenantId) return <Empty msg="Select an MSP tenant" />;

  const inc = detail.data?.incident;
  const narrative = narratives[model];

  return (
    <>
      <PageHeader title="AI Incidents" kicker="XDR Correlation · MITRE ATT&CK" />
      <div className="p-6">
        <Panel>
          <div className="divide-y hairline">
            {data.map((i) => (
              <button key={i.id} data-testid={`incident-${i.id}`}
                onClick={() => setParams({ open: i.id })}
                className="w-full text-left px-4 py-3 hover:bg-white/[0.03] flex items-start gap-4">
                <SevBadge sev={i.severity} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{i.title}</div>
                  <div className="text-xs text-zinc-500">{i.summary}</div>
                  <div className="flex gap-1 mt-1">
                    {i.mitre.map((m) => <span key={m} className="font-mono text-[10px] px-1.5 py-0.5 border hairline text-zinc-400">{m}</span>)}
                  </div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{i.status}</div>
              </button>
            ))}
            {data.length === 0 && <Empty msg="No incidents" />}
          </div>
        </Panel>
      </div>

      <Sheet open={!!openId} onOpenChange={(v) => !v && setParams({})}>
        <SheetContent side="right" className={`bg-[#1B1B28] border-l border-white/10 p-0 transition-[width] duration-300 ${compare ? "w-[1180px] sm:max-w-[1180px]" : "w-[640px] sm:max-w-[640px]"}`}>
          {inc && (
            <>
              <SheetHeader className="px-6 py-4 border-b hairline">
                <SheetTitle className="font-display font-extrabold text-lg tracking-tight flex items-center gap-2">
                  {inc.title} <SevBadge sev={inc.severity} />
                  {inc.confidence && <span className="font-mono text-[10px] text-red-400 border border-red-500/40 px-1.5 py-0.5">ENGINE {Math.round(inc.confidence * 100)}%</span>}
                </SheetTitle>
                <div className="text-xs text-zinc-500 font-mono">{inc.status.toUpperCase()} · {new Date(inc.created_at).toLocaleString()}</div>
              </SheetHeader>
              <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(100vh-90px)]">
                <div className="border border-cyan-400/30 bg-cyan-400/[0.03] p-4">
                  <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-widest">
                      <Brain size={14} /> AI SOC Analyst
                    </div>
                    {!isCustomer && <div className="flex items-center gap-2">
                      <div data-testid="model-picker" className="flex border hairline">
                        {modelList.map((m) => (
                          <button key={m.id} data-testid={`model-${m.id}`} onClick={() => { setModel(m.id); setCompare(false); }}
                            title={m.available ? m.vendor : `${m.vendor} · key required`}
                            className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest transition-colors duration-150 flex items-center gap-1 ${model === m.id && !compare ? "bg-cyan-400 text-[#15151F]" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
                            {m.label}{!m.available && <span className="w-1 h-1 rounded-full bg-orange-400" />}
                          </button>
                        ))}
                        <button data-testid="model-compare" onClick={() => askAI.mutate(true)} disabled={busy}
                          className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest border-l hairline flex items-center gap-1 disabled:opacity-50 transition-colors duration-150 ${compare ? "bg-white text-[#15151F]" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
                          <Columns3 size={11} /> Compare
                        </button>
                      </div>
                      <button data-testid="ai-brief-btn" onClick={() => askAI.mutate(false)} disabled={busy}
                        className="border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 px-3 py-1 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1 disabled:opacity-50 transition-colors duration-150">
                        <Sparkles size={11} /> {busy ? "ANALYZING…" : narrative ? "REGENERATE" : "GENERATE BRIEFING"}
                      </button>
                    </div>}
                  </div>
                  {compare ? (
                    <div data-testid="compare-grid" className="grid grid-cols-3 gap-3">
                      {modelList.map((m) => (
                        <div key={m.id} className="border hairline p-3 min-h-[200px]">
                          <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300 mb-2 border-b hairline pb-1">{m.label} <span className="text-zinc-500">· {m.vendor}</span></div>
                          <div className={`text-xs whitespace-pre-wrap leading-relaxed text-zinc-200 ${busy && !narratives[m.id] ? "ai-pulse" : ""}`}>
                            {narratives[m.id] || <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">{busy ? "Analyzing…" : "No output"}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div data-testid="ai-narrative" className={`text-sm whitespace-pre-wrap leading-relaxed text-zinc-200 min-h-[80px] ${busy ? "ai-pulse" : ""}`}>
                      {narrative || <span className="text-zinc-500 font-mono text-xs uppercase tracking-widest">No briefing from {label(model)} yet. Click GENERATE BRIEFING, or COMPARE to run all three side by side.</span>}
                    </div>
                  )}
                </div>

                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">MITRE ATT&CK</div>
                  <div className="flex flex-wrap gap-2">
                    {inc.mitre.map((m) => (
                      <span key={m} className="font-mono text-xs px-2 py-1 border border-cyan-400/30 text-cyan-300 bg-cyan-400/5">{m}</span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Affected assets</div>
                  <div className="border hairline divide-y divide-white/5">
                    {(detail.data?.endpoints || []).map((e) => (
                      <div key={e.id} className="px-3 py-2 text-xs flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${e.online ? "bg-emerald-400" : "bg-zinc-600"}`} />
                        <span className="font-mono">{e.hostname}</span>
                        <span className="text-zinc-500">{e.user}</span>
                        <span className="ml-auto"><SevBadge sev={e.risk} /></span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Correlated events</div>
                  <div className="border hairline divide-y divide-white/5 max-h-60 overflow-y-auto">
                    {(detail.data?.events || []).slice(0, 15).map((ev) => (
                      <div key={ev.id} className="px-3 py-2 text-xs flex items-start gap-2">
                        <SevBadge sev={ev.severity} />
                        <div className="flex-1">
                          <div>{ev.detail}</div>
                          <div className="font-mono text-[10px] text-zinc-500">{ev.kind} · {ev.process}{ev.mitre ? ` · ${ev.mitre}` : ""}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
